import { useState, useEffect, createContext, useContext } from 'react';

interface AccessibilitySettings {
  fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  highContrast: boolean;
  reducedMotion: boolean;
}

interface AccessibilityContextValue extends AccessibilitySettings {
  updateFontSize: (size: AccessibilitySettings['fontSize']) => void;
  toggleHighContrast: () => void;
  toggleReducedMotion: () => void;
  resetToDefaults: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

const STORAGE_KEY = 'family-app-accessibility';

const defaultSettings: AccessibilitySettings = {
  fontSize: 'medium',
  highContrast: false,
  reducedMotion: false,
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export const useAccessibilityState = () => {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);

  // Load settings from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsedSettings = JSON.parse(stored);
        setSettings({ ...defaultSettings, ...parsedSettings });
      } catch (error) {
        console.error('Failed to parse accessibility settings:', error);
      }
    }

    // Detect system preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)');
    
    if (prefersReducedMotion.matches || prefersHighContrast.matches) {
      setSettings(prev => ({
        ...prev,
        reducedMotion: prefersReducedMotion.matches,
        highContrast: prefersHighContrast.matches,
      }));
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    applyAccessibilityStyles(settings);
  }, [settings]);

  const updateFontSize = (fontSize: AccessibilitySettings['fontSize']) => {
    setSettings(prev => ({ ...prev, fontSize }));
  };

  const toggleHighContrast = () => {
    setSettings(prev => ({ ...prev, highContrast: !prev.highContrast }));
  };

  const toggleReducedMotion = () => {
    setSettings(prev => ({ ...prev, reducedMotion: !prev.reducedMotion }));
  };

  const resetToDefaults = () => {
    setSettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  return {
    ...settings,
    updateFontSize,
    toggleHighContrast,
    toggleReducedMotion,
    resetToDefaults,
  };
};

const applyAccessibilityStyles = (settings: AccessibilitySettings) => {
  const root = document.documentElement;

  // Font size scaling
  const fontSizeMap = {
    'small': '0.9',
    'medium': '1.0',
    'large': '1.1',
    'extra-large': '1.25',
  };
  
  root.style.setProperty('--font-scale', fontSizeMap[settings.fontSize]);

  // High contrast mode
  if (settings.highContrast) {
    root.classList.add('high-contrast');
  } else {
    root.classList.remove('high-contrast');
  }

  // Reduced motion
  if (settings.reducedMotion) {
    root.classList.add('reduced-motion');
  } else {
    root.classList.remove('reduced-motion');
  }
};

// CSS variables and classes for accessibility
export const accessibilityStyles = `
  :root {
    --font-scale: 1.0;
  }

  /* Font size scaling */
  * {
    font-size: calc(var(--font-scale) * 1em);
  }

  /* High contrast mode */
  .high-contrast {
    --color-primary: #0066CC;
    --color-background: #FFFFFF;
    --color-background-secondary: #F0F0F0;
    --color-text-primary: #000000;
    --color-text-secondary: #333333;
    --color-border-light: #666666;
    --color-border-medium: #333333;
    --color-success: #006600;
    --color-warning: #CC6600;
    --color-error: #CC0000;
    
    /* Increase contrast for all interactive elements */
    button, a, input, select, textarea {
      border: 2px solid #000000 !important;
    }
    
    /* Enhance focus indicators */
    *:focus {
      outline: 3px solid #0066CC !important;
      outline-offset: 2px !important;
    }
  }

  /* Reduced motion mode */
  .reduced-motion * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    transform: none !important;
  }

  /* Ensure minimum touch targets */
  @media (pointer: coarse) {
    button, a, input, select, textarea, [role="button"] {
      min-height: 44px;
      min-width: 44px;
    }
  }

  /* Screen reader only content */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .sr-only:focus {
    position: static;
    width: auto;
    height: auto;
    padding: 0.5rem;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
  }

  /* Skip links */
  .skip-link {
    position: absolute;
    top: -40px;
    left: 6px;
    background: #000;
    color: #fff;
    padding: 8px;
    text-decoration: none;
    z-index: 9999;
  }

  .skip-link:focus {
    top: 6px;
  }

  /* Focus management */
  .js-focus-visible *:focus:not(.focus-visible) {
    outline: none;
  }

  /* Improve link visibility */
  a {
    text-decoration: underline;
  }

  /* Ensure form labels are properly associated */
  input:focus + label,
  textarea:focus + label,
  select:focus + label {
    font-weight: bold;
  }
`;

export { AccessibilityContext };
export default useAccessibility;